import { useState, useEffect } from 'react';
import { Flame, ChevronRight, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { BadgeCard } from './BadgeCard';
import { useAuth } from '../../contexts/AuthContext';
import {
  getUserStreak,
  getRecentBadges,
  getNextBadges,
} from '../../services/gamificationService';
import type { UserStreak, UserBadge, BadgeProgress } from '../../types/gamification';
import { logger } from '../../lib/logger';

interface GamificationWidgetProps {
  className?: string;
}

export function GamificationWidget({ className = '' }: GamificationWidgetProps) {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [streak, setStreak] = useState<UserStreak | null>(null);
  const [recentBadges, setRecentBadges] = useState<UserBadge[]>([]);
  const [nextBadges, setNextBadges] = useState<BadgeProgress[]>([]);

  useEffect(() => {
    if (!user) return;

    const loadData = async () => {
      setIsLoading(true);
      try {
        const [streakData, recent, next] = await Promise.all([
          getUserStreak(user.id),
          getRecentBadges(user.id, 3),
          getNextBadges(user.id, 2),
        ]);

        setStreak(streakData);
        setRecentBadges(recent);
        setNextBadges(next);
      } catch (error) {
        logger.error('Error loading gamification data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [user]);

  if (isLoading) {
    return (
      <div className={`bg-white dark:bg-slate-800 rounded-xl border border-gray-100 dark:border-slate-700 p-4 ${className}`}>
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-6 h-6 text-primary animate-spin" />
        </div>
      </div>
    );
  }

  const currentStreak = streak?.current_streak || 0;

  // Determine flame color based on streak
  const getFlameStyles = () => {
    if (currentStreak >= 30) return { color: 'text-violet-500', glow: 'drop-shadow-[0_0_8px_rgba(139,92,246,0.5)]' };
    if (currentStreak >= 14) return { color: 'text-yellow-500', glow: 'drop-shadow-[0_0_8px_rgba(234,179,8,0.5)]' };
    if (currentStreak >= 7) return { color: 'text-orange-500', glow: 'drop-shadow-[0_0_6px_rgba(249,115,22,0.4)]' };
    if (currentStreak >= 3) return { color: 'text-red-500', glow: 'drop-shadow-[0_0_4px_rgba(239,68,68,0.3)]' };
    return { color: 'text-gray-400 dark:text-slate-500', glow: '' };
  };

  const flameStyles = getFlameStyles();

  return (
    <div data-tour="gamification" className={`bg-white dark:bg-slate-800 rounded-xl border border-gray-100 dark:border-slate-700 p-4 ${className}`}>
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
        Dein Fortschritt
      </h3>

      {/* Streak Display */}
      <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-100 dark:border-slate-700">
        <Flame className={`w-8 h-8 ${flameStyles.color} ${flameStyles.glow}`} />
        <div>
          <div className="flex items-baseline gap-1">
            <span className="text-xl font-bold text-gray-900 dark:text-white">
              {currentStreak}
            </span>
            <span className="text-sm text-gray-500 dark:text-slate-400">
              {currentStreak === 1 ? 'Tag' : 'Tage'} Streak
            </span>
          </div>
          {streak?.longest_streak && streak.longest_streak > currentStreak && (
            <p className="text-xs text-gray-400 dark:text-slate-500">
              Bester: {streak.longest_streak} Tage
            </p>
          )}
        </div>
      </div>

      {/* Recent Badges */}
      {recentBadges.length > 0 && (
        <div className="mb-4">
          <p className="text-xs text-gray-500 dark:text-slate-400 mb-2">
            Neueste Badges
          </p>
          <div className="grid grid-cols-3 gap-2">
            {recentBadges.map((ub) => (
              <BadgeCard
                key={ub.id}
                badge={ub.badge!}
                earned={true}
                earnedAt={ub.earned_at}
                size="sm"
              />
            ))}
          </div>
        </div>
      )}

      {/* Next Badges */}
      {nextBadges.length > 0 && (
        <div className="mb-4">
          <p className="text-xs text-gray-500 dark:text-slate-400 mb-2">
            Nächste Badges
          </p>
          <div className="grid grid-cols-2 gap-2">
            {nextBadges.map((bp) => (
              <BadgeCard
                key={bp.badge.id}
                badge={bp.badge}
                earned={false}
                current={bp.current}
                showProgress={true}
                size="sm"
              />
            ))}
          </div>
        </div>
      )}

      {/* No badges yet */}
      {recentBadges.length === 0 && nextBadges.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-slate-400 text-center py-4">
          Starte jetzt und sammle deine ersten Badges!
        </p>
      )}

      {/* Link to Profile */}
      <Link
        to="/profile#badges"
        className="flex items-center justify-center gap-1 mt-2 py-2 text-sm text-primary hover:text-primary-dark transition-colors"
      >
        Alle Badges anzeigen
        <ChevronRight className="w-4 h-4" />
      </Link>
    </div>
  );
}
